import { useMemo, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { FieldValue } from '../../../../../types/QueryBuilder';
import { useLanguage } from '../../../../../context/LanguageContext';
import DualListPanel from './DualList/DualListPanel';
import DualListOptionRow from './DualList/DualListOptionRow';
import { DUAL_LIST_SIDES, DualListSide, SIDE_COLOR } from './DualList/dualListSides';
import { SelectionDialog, SelectionHeader, useDialogFullScreen } from './SelectionDialog';
import { BORDERED_BOX_SX, filterByLabel } from './valueUtils';

interface DualListSelectionDialogEditorProps {
    includeValues: string[];
    excludeValues: string[];
    values: FieldValue[];
    title: string;
    onChange: (data: { value: string[]; notValue: string[] }) => void;
    hideSearch: boolean;
    outerLabels?: Record<DualListSide, string>;
    dialogMaxWidth: 'sm' | 'md';
}

type Selection = Record<DualListSide, string[]>;

const OTHER_SIDE: Record<DualListSide, DualListSide> = { include: 'exclude', exclude: 'include' };

const without = (names: string[], name: string) => names.filter(v => v !== name);

function DualListSelectionDialogEditor({ includeValues, excludeValues, values, title, onChange, hideSearch, outerLabels, dialogMaxWidth }: DualListSelectionDialogEditorProps) {
    const { t } = useLanguage();
    const fullScreen = useDialogFullScreen();
    const committed: Selection = { include: includeValues, exclude: excludeValues };
    const [open, setOpen] = useState(false);
    const [pending, setPending] = useState<Selection>(committed);
    const [search, setSearch] = useState('');

    const labelByName = useMemo(() => new Map(values.map(v => [v.name, v.label])), [values]);
    const toItems = (names: string[]): FieldValue[] => names.map(name => ({ name, label: labelByName.get(name) ?? name }));
    const filteredValues = useMemo(() => (hideSearch ? values : filterByLabel(values, search)), [values, search, hideSearch]);

    const labels: Record<DualListSide, string> = {
        include: t('pages.clients.queryBuilder.simple.dualSelectionDialog.belongsTo'),
        exclude: t('pages.clients.queryBuilder.simple.dualSelectionDialog.doesNotBelongTo'),
    };
    const emptyLabel = t('pages.clients.queryBuilder.simple.selectionDialog.noneSelected');
    const countLabel = (key: string, count: number) =>
        t(`pages.clients.queryBuilder.simple.dualSelectionDialog.${key}${count === 1 ? 'One' : 'Many'}`, { count });
    const summary = `${countLabel('summaryBelongs', includeValues.length)} · ${countLabel('summaryDoesNotBelong', excludeValues.length)}`;

    const handleOpen = () => {
        setPending(committed);
        setSearch('');
        setOpen(true);
    };

    const handleValidate = () => {
        onChange({ value: pending.include, notValue: pending.exclude });
        setOpen(false);
    };

    const addTo = (side: DualListSide, name: string) => setPending(prev => {
        const other = OTHER_SIDE[side];
        return { ...prev, [side]: [...prev[side], name], [other]: without(prev[other], name) };
    });

    const removeFrom = (side: DualListSide, name: string) => setPending(prev => ({ ...prev, [side]: without(prev[side], name) }));

    const removeCommitted = (side: DualListSide, name: string) => {
        const next = { ...committed, [side]: without(committed[side], name) };
        onChange({ value: next.include, notValue: next.exclude });
    };

    const membershipOf = (name: string): DualListSide | null =>
        DUAL_LIST_SIDES.find(side => pending[side].includes(name)) ?? null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
            <SelectionHeader summary={summary} onOpen={handleOpen} />

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5 }}>
                {DUAL_LIST_SIDES.map(side => (
                    <DualListPanel
                        key={side}
                        title={outerLabels?.[side] ?? labels[side]}
                        color={SIDE_COLOR[side]}
                        items={toItems(committed[side])}
                        emptyLabel={emptyLabel}
                        boxSx={{ height: 100 }}
                        onRemove={(name) => removeCommitted(side, name)}
                    />
                ))}
            </Box>

            <SelectionDialog
                open={open}
                title={title}
                maxWidth={dialogMaxWidth}
                height={680}
                fullScreen={fullScreen}
                onCancel={() => setOpen(false)}
                onValidate={handleValidate}
            >
                {!hideSearch && (
                    <TextField
                        size="small"
                        placeholder={t('pages.clients.queryBuilder.searchPlaceholder')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                )}

                <Box sx={{ ...BORDERED_BOX_SX, flexShrink: fullScreen ? 1 : 0, flexGrow: fullScreen ? 1 : 0, height: fullScreen ? 'auto' : 220, overflowY: 'auto' }}>
                    {filteredValues.length === 0 ? (
                        <Typography variant="body2" sx={{ color: 'text.secondary', px: 1, py: 1 }}>
                            {t('pages.clients.queryBuilder.simple.noOptionsFound')}
                        </Typography>
                    ) : (
                        filteredValues.map(option => (
                            <DualListOptionRow
                                key={option.name}
                                option={option}
                                membership={membershipOf(option.name)}
                                labels={labels}
                                onAdd={(side) => addTo(side, option.name)}
                                onRemove={(side) => removeFrom(side, option.name)}
                            />
                        ))
                    )}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, flexGrow: 1, minHeight: 0 }}>
                    {DUAL_LIST_SIDES.map(side => (
                        <DualListPanel
                            key={side}
                            title={labels[side]}
                            color={SIDE_COLOR[side]}
                            items={toItems(pending[side])}
                            emptyLabel={emptyLabel}
                            boxSx={{ flexGrow: 1, minHeight: 80 }}
                            chips
                            onRemove={(name) => removeFrom(side, name)}
                        />
                    ))}
                </Box>
            </SelectionDialog>
        </Box>
    );
}

export default DualListSelectionDialogEditor;
